import { SpotUserSingleCircleOutlineIcon } from '@tableau/icons/SpotUserSingleCircleOutlineIcon';
import { DialogPositioningBehavior, DialogType, DialogWidget, InlineAlertType, InlineAlertWidget } from '@tableau/widgets';
import React from 'react';

import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { ProjectDefinition } from '~src/projects/components/contentDefinitions/ProjectDefinition';
import { populateContentBreadcrumbs } from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbHelpers';
import { ConnectedExplorer } from '~src/spacesPlatform/components/Explorer/ConnectedExplorer';
import { CreateContentButtonLocations, CreateContentButtonOwnProps } from '~src/spacesPlatform/components/Explorer/CreateContent/CreateContentButton';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { ContentSpecificControls, ExplorerToolbarTestIds } from '~src/spacesPlatform/components/Explorer/ExplorerToolbar';
import { FilterByTypeDropdown } from '~src/spacesPlatform/components/Explorer/FilterByTypeDropdown';
import { ExplorerEmptyStateProps } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerEmpyState';
import { CompactSpaceHeaderContent } from '~src/spacesPlatform/components/header/CompactSpaceHeaderContent';
import { SpaceHeaderContent } from '~src/spacesPlatform/components/header/SpaceHeaderContent';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { createContentButton } from '~src/spacesPlatform/components/spaceComponents/SubspaceHelpers';
import { getPersonalSpaceContentInOrder } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { NullProjectIdForNonProjects, SpaceType } from '~src/spacesPlatform/state/SpaceInfo';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';

import { Messages } from '../../../codegen/Localize';
import { FetchDraftsRequestedAction } from '../../../drafts/actions/DraftsActions';
import { getDraftsRequest } from '../../../drafts/requests/DraftsRequests';
import { Draft } from '../../../drafts/types/DraftsTypes';
import { ExplorableItemType } from '../../../spacesPlatform/models/ContentType';
import { FilterMap } from '../../../spacesPlatform/state/ContentExplorerState';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '../../../spacesPlatform/state/SpaceInfo';
import { PersonalSpaceInfo } from '../../restApi/PersonalSpace';
import { PersonalSpaceStyles } from './PersonalSpaceStyles';

const testIdPrefix = 'personal-space';
const EMPTY_STATE_TEST_ID = `${testIdPrefix}-empty-state`;

export interface PersonalSpaceOwnProps {
  readonly urls: SpacesSiteUrls;
}

export interface PersonalSpaceStateProps {
  readonly contentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly personalSpace: PersonalSpaceInfo;
  readonly siteName: string;
}

export interface PersonalSpaceDispatchProps {
  readonly requestSpaceContents: () => void;
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly clearContentCounts: (spaceType: SpaceType) => void;
  readonly getPersonalSpace: () => void;
  readonly createNewWorkbook: (projectId?: string) => void;
}

export type PersonalSpaceProps = PersonalSpaceStateProps
  & PersonalSpaceDispatchProps
  & PersonalSpaceOwnProps;

interface PersonalSpaceState {
  isDraftDeletionDialogOpen: boolean;
  drafts: Draft[];
  isLoadingDrafts: boolean;
  draftsError: string | null;
}

export class PersonalSpace extends React.PureComponent<PersonalSpaceProps, PersonalSpaceState> {
  constructor(props: PersonalSpaceProps) {
    super(props);
    this.state = {
      isDraftDeletionDialogOpen: false,
      drafts: [],
      isLoadingDrafts: false,
      draftsError: null
    };
  }

  public componentDidMount() {
    BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: Messages.personalSpaceTitle() });
    this.props.requestSpaceContents();
    this.props.getPersonalSpace();
    // This is needed so the breadcrumb if set is cleared when personal page is loaded.
    populateContentBreadcrumbs([], this.props.urls);
  }

  private openDraftDeletionDialog = async () => {
    if (!FeatureFlags.EnableDraftExpirationEnabled) {
      return;
    }
    this.setState({ isDraftDeletionDialogOpen: true, isLoadingDrafts: true, draftsError: null });
    try {
      // Create the action object needed for getDraftsRequest
      const spaceInfo: SpaceInfo = {
        spaceType: 'personal',
        item: SpaceItemForNonEntitySpace,
        subspaceName: undefined
      };
      const filters: FilterMap = {};
      const contentTypes: ReadonlyArray<ExplorableItemType> = ['workbook'];
      const action: FetchDraftsRequestedAction = {
        type: 'FETCH_DRAFTS_REQUESTED',
        spaceInfo,
        filters,
        contentTypes,
        shouldDispatchDraftActions: false
      };
      const drafts = await getDraftsRequest(action);
      this.setState({ drafts, isLoadingDrafts: false });
    } catch (error) {
      this.setState({
        draftsError: 'Failed to load drafts',
        isLoadingDrafts: false
      });
    }
  };

  private closeDraftDeletionDialog = () => {
    this.setState({ isDraftDeletionDialogOpen: false });
  };

  private renderDraftDeletionDialog = (): JSX.Element => {
    const { drafts, isLoadingDrafts, draftsError } = this.state;

    // Filter drafts that were updated more than 2 years ago
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

    const oldDrafts = drafts.filter(draft => {
      const lastUpdated = new Date(draft.updatedAt);
      return lastUpdated < twoYearsAgo;
    });

    return (
      <DialogWidget
        testId="draft-deletion-dialog"
        title={Messages.personalSpaceDeletionDialogTitle()}
        type={DialogType.NormalWithTitleBar}
        isModal={true}
        onCloseRequest={this.closeDraftDeletionDialog}
        positioningBehavior={DialogPositioningBehavior.Centered}
        showCloseButton={true}
        showFooter={false}
        dialogWidth={600}
        dialogHeight={400}
      >
        <div style={{ padding: '20px' }}>
          {isLoadingDrafts ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              Loading drafts...
            </div>
          ) : draftsError ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#d32f2f' }}>
              {draftsError}
            </div>
          ) : oldDrafts.length > 0 ? (
            <>
              <p style={{ marginTop: '-15px', marginBottom: '20px', fontSize: '14px' }}>
                {Messages.personalSpaceDeletionDialogIntro()}
                <br />
                {Messages.personalSpaceDeletionDialogWarning()}
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px' }}>
                {Messages.personalSpaceDeletionDialogPrevention()}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', fontWeight: 'bold', borderBottom: '1px solid #ccc', paddingBottom: '8px' }}>
                <div style={{ flex: 1, paddingRight: '8px' }}>{Messages.personalSpaceDeletionDialogTableHeaderDraftName()}</div>
                <div style={{ flex: 1, paddingLeft: '8px' }}>{Messages.personalSpaceDeletionDialogTableHeaderLastUpdated()}</div>
              </div>
                {oldDrafts.map((draft, index) => {
                  const lastUpdated = new Date(draft.updatedAt);

                  // Use site name from Redux state
                  const siteUrlName = this.props.siteName;
                  const draftEditUrl = `/t/${siteUrlName}/newWorkbook/${draft.draftReferenceId}`;
                  return (
                    <div key={draft.draftLuid || index} style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <div style={{ flex: 1, paddingRight: '8px' }}>
                        <a
                          href={draftEditUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#0067C7', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                        >
                          {draft.name}
                        </a>
                      </div>
                      <div style={{ flex: 1, paddingLeft: '8px' }}>{lastUpdated.toLocaleDateString()}</div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              {Messages.personalSpaceDeletionDialogNoDraftsFound()}
            </div>
          )}
        </div>
      </DialogWidget>
    );
  };

  public render(): JSX.Element {
    const createContentButtonProps: CreateContentButtonOwnProps = {
      location: CreateContentButtonLocations.PersonalSpace,
      projectId: NullProjectIdForNonProjects,
      spaceType: 'personal'
    };
    const toolbarContentSpecificControlsProps: ContentSpecificControls = { beginning: this.props.personalSpace.readOnly ? undefined : createContentButton(createContentButtonProps) };
    return (
      <div className={SpaceContainerStyles.SpaceContainer}>
        {
          FeatureFlags.compactHeaderEnabled ?
            <CompactSpaceHeaderContent headerText={Messages.personalSpaceTitle()} renderSubHeadingArea={() => <>
              <div style={SpaceContainerStyles.MastheadSubheadingArea}>
                {Messages.personalSpaceMastheadSubheading()}
                {FeatureFlags.EnableDraftExpirationEnabled && (
                  <>
                    <br />
                    {Messages.personalSpaceDeletionNotePrefix()}
                    <button type="button" className={PersonalSpaceStyles.linkButton} onClick={(e) => { e.preventDefault(); this.openDraftDeletionDialog(); }}>
                      {Messages.personalSpaceDeletionNoteLinkText()}
                    </button>
                    {Messages.personalSpaceDeletionNoteSuffix()}
                  </>
                )}
              </div>
              {this.personalSpaceWarning(this.props.personalSpace.readOnly)}
            </>} />
            : <div className={SpaceContainerStyles.MastheadArea}>
              <SpaceHeaderContent headerText={Messages.personalSpaceTitle()} />
              <div style={SpaceContainerStyles.MastheadSubheadingArea}>
                {Messages.personalSpaceMastheadSubheading()}
                {FeatureFlags.EnableDraftExpirationEnabled && (
                  <>
                    <br />
                    {Messages.personalSpaceDeletionNotePrefix()}
                    <button type="button" className={PersonalSpaceStyles.linkButton} onClick={(e) => { e.preventDefault(); this.openDraftDeletionDialog(); }}>
                      {Messages.personalSpaceDeletionNoteLinkText()}
                    </button>
                    {Messages.personalSpaceDeletionNoteSuffix()}
                  </>
                )}
              </div>
              {this.personalSpaceWarning(this.props.personalSpace.readOnly)}
            </div>
        }
        <div className={SpaceContainerStyles.SubplaceArea}>
          <ConnectedExplorer
            supportedDisplayModes={ProjectDefinition.supportedDisplayModes}
            emptyStateProps={this.emptyStateProps()}
            toolbarProps={{
              supportFilterPanel: true,
              toolbarContentFilterControl: undefined,
              toolbarVisibility: FeatureFlags.compactHeaderEnabled ? 'hidden' : 'enabled',
              hideContentSpecificControlsInEmptyState: true,
              toolbarContentSpecificControls: toolbarContentSpecificControlsProps,
            }}
          />
        </div>
        {FeatureFlags.EnableDraftExpirationEnabled && this.state.isDraftDeletionDialogOpen && this.renderDraftDeletionDialog()}
      </div>
    );
  }

  private contentFilterControl(): JSX.Element {
    return (
      <React.Fragment>
        <span id={ExplorerToolbarTestIds.FilterByTypeDropdownButtonLabel} className={ExplorerStyles.ToolbarDropdownLabel}>
          {Messages.filterByTypeDropdown_label()}
        </span>
        <FilterByTypeDropdown
          filterSelected={this.props.filterSelected}
          contentFilter={this.props.contentTypeFilterValues}
          defaultContentTypeFilterValues={this.props.defaultContentTypeFilterValues}
          showWorkbooksAsViews={true}
          clearContentCounts={this.props.clearContentCounts}
          spaceType={'personal'}
          contentItemDefinitions={getPersonalSpaceContentInOrder()}
        />
      </React.Fragment>
    );
  }

  private personalSpaceWarning(readonly: boolean) {
    if (!readonly) {
      return null;
    }
    return (
      <InlineAlertWidget
        testId={'personalspace-warning'}
        type={InlineAlertType.Warning}
        style={{ marginTop: '12px' }}
      >
        {Messages.personalSpaceReadOnlyWarning()}
      </InlineAlertWidget>
    );
  }

  private emptyStateProps(): ExplorerEmptyStateProps {
    return {
      icon: SpotUserSingleCircleOutlineIcon,
      primaryMessage: Messages.personalSpaceEmptyStatePrimary(),
      secondaryMessage: Messages.personalSpaceEmptyStateSecondary(),
      dataTestId: EMPTY_STATE_TEST_ID,
      actionButtonMessage: this.props.personalSpace.readOnly ? undefined : Messages.personalSpaceEmptyStateActionButton(),
      onActionButton: () => this.props.createNewWorkbook(),
    };
  }
}
